import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from 'src/app/core/services/AuthService/auth.service';
import { HttpRequest, HttpHandlerFn, HttpEvent } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, from } from 'rxjs';
import { catchError, switchMap, filter, take, tap } from 'rxjs/operators';

export const tokenInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  const authService = inject(AuthService);

  // Wrap getTokenAsync in an observable to handle it as a stream
  return from(authService.getTokenAsync()).pipe(
    switchMap((token) => {
      let clonedReq = req;

      if (token && !authService.isTokenExpired(token)) {
        clonedReq = req.clone({
          setHeaders: { Authorization: `Bearer ${token}` },
        });
      }

      return next(clonedReq).pipe(
        catchError((error) => handleError(error, authService, req, next))
      );
    })
  );
};

// Singleton BehaviorSubject to manage token refresh status
let isRefreshing = false;
const tokenRefreshSubject = new BehaviorSubject<string | null>(null);

function handleError(
  error: HttpErrorResponse,
  authService: AuthService,
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> {
  if (error.status === 401) {
    authService.notifyUser('Sessão expirada. Tentando renovar o token...');
    return handleTokenRefresh(authService, req, next);
  }

  notifyError(error, authService);
  return throwError(() => error);
}

function handleTokenRefresh(
  authService: AuthService,
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> {
  return from(authService.getRefreshToken()).pipe(
    switchMap((refreshToken) => {
      if (!refreshToken) {
        authService.logout();
        return throwError(() => new Error('Session expired. Please log in again.'));
      }

      if (isRefreshing) {
        // Wait for the new token from the refresh
        return tokenRefreshSubject.pipe(
          filter((token) => token !== null),
          take(1),
          switchMap((newToken) => retryRequestWithNewToken(newToken as string, req, next))
        );
      } else {
        isRefreshing = true;

        return from(authService.refreshToken(refreshToken)).pipe(
          tap((newToken: string | null) => {
            isRefreshing = false;

            if (newToken) {
              authService.setToken(newToken);
              tokenRefreshSubject.next(newToken); // Notify waiting requests
            } else {
              tokenRefreshSubject.next(null); // Notify waiting requests to stop
              authService.logout();
            }
          }),
          switchMap((newToken) => {
            if (!newToken) {
              return throwError(() => new Error('Invalid refresh token.'));
            }
            return retryRequestWithNewToken(newToken, req, next);
          }),
          catchError((refreshError) => {
            isRefreshing = false;
            tokenRefreshSubject.next(null);
            authService.logout();
            return throwError(() => new Error('Failed to refresh token.'));
          })
        );
      }
    })
  );
}

function retryRequestWithNewToken(
  newToken: string,
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> {
  const clonedReq = req.clone({
    setHeaders: { Authorization: `Bearer ${newToken}` },
  });
  return next(clonedReq).pipe(
    catchError((retryError) => {
      console.error('Error during request retry:', retryError);
      return throwError(() => retryError);
    })
  );
}

function notifyError(error: HttpErrorResponse, authService: AuthService): void {
  switch (error.status) {
    case 403:
      authService.notifyUser('Você não tem permissão para acessar este recurso.');
      break;
    case 500:
      authService.notifyUser('Erro no servidor. Tente novamente mais tarde.');
      break;
    default:
      authService.notifyUser('Ocorreu um erro inesperado. Tente novamente.');
  }
}